<template>
    <div id="kollus-player-launcher">
        <template v-if="(message.length > 0)">
            <div class="message"><p>{{ message }}</p></div>
        </template>
        <template v-else>
            <component :is="currentView"
                       :player-policy="playerPolicy"
                       :media-info="mediaInfo"
                       :media-info-string="mediaInfoString"
                       :expire-time="expireTime">
            </component>
        </template>
    </div>
</template>

<script>
import KollusPlayerV2 from './KollusPlayer.v2';
import KollusPlayerV3 from './KollusPlayer.v3';
import KollusPlayerV4 from './KollusPlayer.v4';
import KollusPlayerV5 from './KollusPlayer.v5';
import KollusPlayerFlash from './KollusPlayer.flash';
import KollusPlayerMac from './KollusPlayer.mac';
import KollusPlayerMobile from './KollusPlayer.mobile';
import UAParser from 'ua-parser-js';
import PlayerVersionValidation from './validation/playerVersionValidation';

export default {
    props: {
        playerPolicy: {
            type: Object,
            required: true,
        },
        mediaInfo: {
            type: Object,
            default: () => {},
        },
        mediaInfoString: {
            type: String,
            default: '',
        },
        expireTime: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            currentView: '',
            message: '',
        };
    },
    components: {
        'kollus-player-v2': KollusPlayerV2,
        'kollus-player-v3': KollusPlayerV3,
        'kollus-player-v4': KollusPlayerV4,
        'kollus-player-v5': KollusPlayerV5,
        'kollus-player-flash': KollusPlayerFlash,
        'kollus-player-mac': KollusPlayerMac,
        'kollus-player-mobile': KollusPlayerMobile,
    },
    mounted() {
        // vue-18n test
        // console.log(this.$i18n.t('kollus_player.media_result.http_status_code.0'));
        const uaParser = new UAParser();

        this.playerPolicy.os = uaParser.getOS();
        this.playerPolicy.browser = uaParser.getBrowser();
        this.playerPolicy.device = uaParser.getDevice();
        this.playerPolicy.isSupportedMSE = !!window.MediaSource;
        this.playerPolicy.isDaumNaverKitKatWebView =
            uaParser.getUA().indexOf('naver') !== -1 ||
            uaParser.getUA().indexOf('daum') !== -1;

        if (this.playerPolicy.os.name === 'Windows' && this.playerPolicy.browser.name === 'IE') {
            this.notSupportedBrowser();
        }

        // launch kollus player
        if (this.playerPolicy.use_exclusive_player) {
            this.launchExclusivePlayer();
        } else {
            this.launchNonExclusivePlayer();
        }
    },
    methods: {
        /**
         * launchKollusPlayer
         */
        launchExclusivePlayer() {
            const OS = this.playerPolicy.os;
            const BROWSER = this.playerPolicy.browser;
            let playerVersion = this.playerPolicy.player_version;

            if (typeof playerVersion === 'undefined' ||
                (typeof playerVersion === 'string' && playerVersion === 'exclusive')
            ) {
                if (OS.name === 'Android') {
                    playerVersion = 'android';
                } else if (OS.name === 'iOS') {
                    playerVersion = 'ios';
                } else if (OS.name === 'Mac OS') {
                    playerVersion = 'mac';
                } else if (OS.name === 'Windows') {
                    playerVersion = 'windows';
                } else if (OS.name === 'Linux' && BROWSER.name === 'Samsung Browser') { // LG G-Pad
                    playerVersion = 'android';
                }
            } else if (playerVersion === 'pc') {
                if (OS.name === 'Mac OS') {
                    playerVersion = 'mac';
                } else if (OS.name === 'Windows') {
                    playerVersion = 'windows';
                } else {
                    this.notSupportedPlayer();
                }
            } else if (playerVersion === 'mobile') {
                if (OS.name === 'Android') {
                    playerVersion = 'android';
                } else if (OS.name === 'iOS') {
                    playerVersion = 'ios';
                } else {
                    this.notSupportedPlayer();
                }
            }

            if (playerVersion === 'windows') {
                if (this.playerPolicy.is_live) {
                    playerVersion = 'v4a';

                    if (OS.name === 'Windows' && BROWSER.name === 'IE') {
                        if (Number(BROWSER.major) <= 10 || Number(OS.version) < 8.1) {
                            playerVersion = 'v3e';
                        }
                    }
                } else {
                    playerVersion = 'v3';
                    if (OS.name === 'Windows' && BROWSER.name === 'IE') {
                        if (Number(BROWSER.major) <= 6) {
                            playerVersion = 'v3e';
                        } else if (Number(BROWSER.major) <= 8) {
                            playerVersion = 'v2';
                        }
                    }
                }
            }

            // 최종적으로 설정된 playerVersion값을 validation하여 유효하지 않은 경우 notSupportedPlayer 페이지를 뿌려준다.
            playerVersion = PlayerVersionValidation.validate(this.playerPolicy, playerVersion);

            if (playerVersion === 'mac' && this.isIosTouchPointCheck()) {
                playerVersion = 'ios';
                OS.name = 'iOS';
            }

            // 삼성브라우저인 경우 PC모드가 추가가 되어 모바일로 전환하도록 수정
            // 2021.01.26 marufactory
            if (this.isMobileSamgsungBrowser()) {
                playerVersion = 'android';
                OS.name = 'Android';
            }

            switch (playerVersion) {
            case 'ios':
            case 'android':
                // console.log('launch mobile player!!');
                this.currentView = 'kollus-player-mobile';
                break;
            case 'v2':
                // console.log('launch v2 activex player!!');
                this.currentView = 'kollus-player-v2';
                break;
            case 'v3':
            case 'v3e':
                // console.log('launch v3/v3e player!!');
                if (this.playerPolicy.use_v5_player) {
                    this.currentView = 'kollus-player-v5';
                } else {
                    this.currentView = 'kollus-player-v3';
                }
                break;
            case 'mac':
                // console.log('launch mac player!!');
                this.currentView = 'kollus-player-mac';
                break;
            case 'v4a':
                // console.log('launch v4 player with KollusAgent!!');
                if (this.playerPolicy.use_v5_player) {
                    this.currentView = 'kollus-player-v5';
                } else {
                    this.currentView = 'kollus-player-v4';
                }
                break;
            case 'v5a':
                this.currentView = 'kollus-player-v5';
                break;
            default:
                this.notSupportedPlayer();
                break;
            }
        },
        launchNonExclusivePlayer() {
            const OS = this.playerPolicy.os;
            const BROWSER = this.playerPolicy.browser;
            const playerVersion = this.playerPolicy.player_version;

            if (typeof playerVersion === 'undefined' || playerVersion == null) {
                // Window / IE인 경우
                // if (OS.name === 'Windows' && BROWSER.name === 'IE') {
                //     // Window 버전이 8.1 미만이거나, Live이면서 IE 11 미만인경우 거나, IE9 미만이면 Flash Fallback.
                //     if (Number(OS.version) < 8.1 || (this.playerPolicy.is_live && Number(BROWSER.major) < 11) || Number(BROWSER.major) < 9) {
                //         //playerVersion = 'flash';
                //     }
                // // Mobile Device인 경우는 html5 player로 설정한다.
                // } else if (OS.name === 'Android' || OS.name === 'iOS') {
                //     playerVersion = 'html5';
                // } else {
                //     // Live 컨텐츠이고, 브라우저가 MSE를 지원하지 않으면 FlashPlayer로 Fallback 시킨다.
                //     if (this.playerPolicy.is_live && !this.playerPolicy.isSupportedMSE) {
                //         playerVersion = 'flash';
                //     }
                // }

                // Window / IE인 경우
                // IE 는 비암호화도 지원하지 않도록 변경.
                // if (OS.name === 'Windows' && BROWSER.name === 'IE') {
                //     // Window 버전이 8.1 미만이거나, Live이면서 IE 11 미만인경우 거나, IE9 미만이면 지원하지 않는 브라우저라고 표기한다.
                //     if (Number(OS.version) < 8.1 || (this.playerPolicy.is_live && Number(BROWSER.major) < 11) || Number(BROWSER.major) < 9) {
                //         this.notSupportedBrowser();
                //     }
                // }
            }

            switch (playerVersion) {
            case 'flash':
                // console.log('launch flash player!!');
                this.currentView = 'kollus-player-flash';
                break;
            case 'v5':
                this.currentView = 'kollus-player-v5';
                break;
            default:
                if (this.playerPolicy.use_v5_player) {
                    this.currentView = 'kollus-player-v5';
                } else {
                // console.log('launch html5 player!!');
                    this.currentView = 'kollus-player-v4';
                }
                break;
            }
        },
        notSupportedBrowser() {
            this.message = this.$i18n.t('kollus_player.notify_message.not_supported_browser');
        },
        notSupportedPlayer() {
            // console.log('not supported player!!');
            this.message = this.$i18n.t('kollus_player.media_result.http_status_code.0');
        },
        // IOS 13 버전부터 아이패드가 mac으로 agent 를 가져오는 이슈 때문에 추가
        isIosTouchPointCheck() {
            return new RegExp('(Macintosh)', 'i').test(navigator.userAgent) && navigator.maxTouchPoints > 0;
        },
        isMobileSamgsungBrowser() {
            return new RegExp('(SamsungBrowser)', 'i').test(navigator.userAgent) && navigator.maxTouchPoints > 0;
        },
    },
};
</script>

